import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Form, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { LuUser, LuMail, LuPhone, LuMapPin, LuSave, LuCircleCheck, LuBuilding, LuArrowRight } from "react-icons/lu";
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import { toast } from 'react-toastify';
import PhoneInput from '../../components/common/PhoneInput';
import { fetchMyPortalBusinesses, selectPortalBusiness } from '../../redux/reducers/portalAuthSlice';

const PortalProfile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { customer, myBusinesses } = useSelector((state) => state.portalAuth);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: { countryCode: '+1', number: '' },
        address: { street: '', city: '', state: '', zipCode: '', country: '' }
    });

    const fetchProfile = async () => {
        setFetching(true);
        try {
            const res = await PortalAxios.PortalRequest({
                method: 'get',
                url: `${baseURL}/portal/customer/profile`
            });
            const data = res.data.data;
            setProfile({
                ...data,
                phone: typeof data.phone === 'string' 
                    ? { countryCode: '+1', number: data.phone } 
                    : data.phone || { countryCode: '+1', number: "" }
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load profile');
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchProfile();
        dispatch(fetchMyPortalBusinesses());
    }, [dispatch]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await PortalAxios.PortalRequest({
                method: 'patch',
                url: `${baseURL}/portal/customer/profile`,
                data: {
                    name: profile.name,
                    phone: profile.phone,
                    address: profile.address
                }
            });
            toast.success('Profile updated successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setProfile(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setProfile(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSwitchBusiness = (biz) => {
        if (biz.businessId === customer?.businessId) return;

        const payload = {
            token: biz.token,
            customer: {
                name: biz.customerName,
                email: customer.email,
                businessName: biz.businessName,
                businessId: biz.businessId
            }
        };
        dispatch(selectPortalBusiness(payload));
        toast.success(`Switched to ${biz.businessName} portal`);
        navigate(`/customer-side/${biz.businessId}/dashboard`);
    };

    if (fetching) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;

    return (
        <div>
            <div className="mb-4">
                <h2 className="fw-bold text-dark mb-1">My Account</h2>
                <p className="text-muted mb-0">Manage your personal details and delivery information.</p>
            </div>

            <Row>
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Header className="bg-white border-0 py-3">
                            <div className="d-flex align-items-center gap-2">
                                <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                                <h6 className="fw-bold text-dark mb-0">Personal Information</h6>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
                            
                            <Form onSubmit={handleUpdate}>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold text-muted mb-1">Full Name</Form.Label>
                                            <InputGroup className="bg-light rounded-3 overflow-hidden">
                                                <InputGroup.Text className="bg-transparent border-0 pe-0"><LuUser size={18} className="text-muted" /></InputGroup.Text>
                                                <Form.Control 
                                                    className="bg-transparent border-0 py-2 shadow-none"
                                                    value={profile.name}
                                                    onChange={(e) => handleChange('name', e.target.value)}
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold text-muted mb-1">Email Address</Form.Label>
                                            <InputGroup className="bg-light rounded-3 overflow-hidden opacity-75">
                                                <InputGroup.Text className="bg-transparent border-0 pe-0"><LuMail size={18} className="text-muted" /></InputGroup.Text>
                                                <Form.Control 
                                                    className="bg-transparent border-0 py-2 shadow-none"
                                                    value={profile.email}
                                                    disabled
                                                />
                                            </InputGroup>
                                            <Form.Text className="text-muted" style={{ fontSize: '10px' }}>Email cannot be changed by customer</Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <PhoneInput
                                            label="Phone Number"
                                            value={profile.phone}
                                            onChange={(val) => handleChange('phone', val)}
                                        />
                                    </Col>

                                    <div className="border-top mt-4 pt-4 mb-2">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <LuMapPin size={18} className="text-muted" />
                                            <h6 className="fw-bold text-dark mb-0">Delivery Address</h6>
                                        </div>
                                        
                                        <Row className="g-3">
                                            <Col xs={12}>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">Street Address</Form.Label>
                                                    <Form.Control 
                                                        className="bg-light border-0 rounded-3 py-2"
                                                        value={profile.address?.street || ''}
                                                        onChange={(e) => handleChange('address.street', e.target.value)}
                                                        placeholder="123 Main St"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">City</Form.Label>
                                                    <Form.Control 
                                                        className="bg-light border-0 rounded-3 py-2"
                                                        value={profile.address?.city || ''}
                                                        onChange={(e) => handleChange('address.city', e.target.value)}
                                                        placeholder="Toronto"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">State / Province</Form.Label>
                                                    <Form.Control 
                                                        className="bg-light border-0 rounded-3 py-2"
                                                        value={profile.address?.state || ''}
                                                        onChange={(e) => handleChange('address.state', e.target.value)}
                                                        placeholder="ON"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">Postal Code / ZIP</Form.Label>
                                                    <Form.Control 
                                                        className="bg-light border-0 rounded-3 py-2"
                                                        value={profile.address?.zipCode || ''}
                                                        onChange={(e) => handleChange('address.zipCode', e.target.value)}
                                                        placeholder="M5V 2H1"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small text-muted mb-1">Country</Form.Label>
                                                    <Form.Control 
                                                        className="bg-light border-0 rounded-3 py-2"
                                                        value={profile.address?.country || ''}
                                                        onChange={(e) => handleChange('address.country', e.target.value)}
                                                        placeholder="Canada"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="text-end mt-4">
                                        <Button 
                                            type="submit" 
                                            className="rounded-3 px-4 py-2 fw-bold text-white border-0"
                                            style={{ background: "#28B0C4" }}
                                            disabled={loading}
                                        >
                                            {loading ? <Spinner animation="border" size="sm" /> : <><LuSave size={18} className="me-2" /> Save Changes</>}
                                        </Button>
                                    </div>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-4 text-center mb-4">
                        <div className="mx-auto rounded-circle d-flex align-items-center justify-content-center fw-bold text-white mb-3"
                             style={{ width: 80, height: 80, fontSize: 32, backgroundColor: "#28B0C4" }}>
                            {profile.name?.charAt(0).toUpperCase()}
                        </div>
                        <h5 className="fw-bold text-dark mb-1">{profile.name}</h5>
                        <p className="text-muted small mb-4">{profile.email}</p>
                        
                        <div className="text-start bg-light rounded-4 p-3 border">
                            <div className="d-flex align-items-center gap-2 mb-2 text-success">
                                <LuCircleCheck size={16} />
                                <span className="small fw-bold">Active Portal Account</span>
                            </div>
                            <p className="extra-small text-muted mb-0">Your profile information is used for order confirmations and delivery updates.</p>
                        </div>
                    </Card>

                    {myBusinesses && myBusinesses.length > 1 && (
                        <Card className="border-0 shadow-sm rounded-4 p-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                                <h6 className="fw-bold text-dark mb-0">Switch Portal</h6>
                            </div>
                            <p className="text-muted small mb-3">You are enrolled in multiple businesses. Switch to another business portal below:</p>
                            <div className="d-flex flex-column gap-2">
                                {myBusinesses.map((biz) => {
                                    const isActive = biz.businessId === customer?.businessId;
                                    return (
                                        <div 
                                            key={biz.businessId}
                                            onClick={() => !isActive && handleSwitchBusiness(biz)}
                                            className={`d-flex align-items-center justify-content-between p-3 rounded-3 border ${isActive ? 'bg-light' : 'bg-white hover-switch cursor-pointer'}`}
                                            style={{
                                                transition: 'all 0.2s ease',
                                                borderColor: isActive ? 'rgba(40, 176, 196, 0.4)' : 'rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                {biz.logo ? (
                                                    <img 
                                                        src={biz.logo} 
                                                        alt={biz.businessName} 
                                                        className="rounded-2 object-fit-cover"
                                                        style={{ width: '32px', height: '32px' }}
                                                    />
                                                ) : (
                                                    <div className="rounded-2 d-flex align-items-center justify-content-center fw-bold text-white"
                                                         style={{ width: '32px', height: '32px', backgroundColor: '#28B0C4', fontSize: '12px' }}>
                                                        {biz.businessName.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="fw-bold text-dark small">{biz.businessName}</div>
                                                    <div className="text-muted extra-small">{biz.customerName}</div>
                                                </div>
                                            </div>
                                            {isActive ? (
                                                <span className="badge text-white px-2 py-1 rounded-2" style={{ backgroundColor: '#28B0C4', fontSize: '9px' }}>Active</span>
                                            ) : (
                                                <LuArrowRight size={14} className="text-muted arrow-icon" style={{ transition: 'transform 0.2s' }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <style>{`
                                .hover-switch:hover {
                                    background-color: rgba(40, 176, 196, 0.04) !important;
                                    border-color: rgba(40, 176, 196, 0.2) !important;
                                }
                                .hover-switch:hover .arrow-icon {
                                    transform: translateX(3px);
                                    color: #28B0C4 !important;
                                }
                                .extra-small {
                                    font-size: 10px;
                                }
                            `}</style>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default PortalProfile;
